import React, { useState, useEffect } from 'react';

/**
 * CourseSelector - Displays and manages course selection with polling
 * Handles different course structures for different activity types
 */
const CourseSelector = ({
  activityType,
  activityName,
  courses,
  courseStructure,
  maxCourses,
  selectedCourses,
  onCourseToggle,
  isLoading,
  error,
  isTestMode
}) => {
  const [localSelectedCourses, setLocalSelectedCourses] = useState(new Set(selectedCourses || []));

  useEffect(() => {
    setLocalSelectedCourses(new Set(selectedCourses || []));
  }, [selectedCourses]);

  const handleToggle = (courseId) => {
    const newSelected = new Set(localSelectedCourses);
    if (newSelected.has(courseId)) {
      newSelected.delete(courseId);
    } else {
      newSelected.add(courseId);
    }
    setLocalSelectedCourses(newSelected);
    onCourseToggle(Array.from(newSelected));
  };

  const renderCourseCheckbox = (course, courseId, courseName) => {
    const isSelected = localSelectedCourses.has(courseId);
    const isDisabled = isLoading || (maxCourses > 0 && localSelectedCourses.size >= maxCourses && !isSelected);

    // Get XP value - Duolingo uses 'xp', others might use 'score'
    const xpValue = course.xp !== undefined ? course.xp : course.score;

    // Get level completion progress - CodeCombat uses 'completed_levels' and 'total_levels'
    const completedLevels = course.completed_levels;
    const totalLevels = course.total_levels;
    const hasLevelProgress = completedLevels !== undefined && totalLevels !== undefined;

    return (
      <div key={courseId} className="form-check border rounded p-3 mb-2">
        <input
          type="checkbox"
          className="form-check-input course-checkbox"
          id={`course-${courseId}`}
          value={courseId}
          checked={isSelected}
          onChange={() => handleToggle(courseId)}
          disabled={isDisabled}
          data-course-name={courseName}
          {...(isTestMode && { 'data-testid': `course-checkbox-${courseId}` })}
        />
        <label className="form-check-label w-100" htmlFor={`course-${courseId}`}>
          <div className="d-flex justify-content-between align-items-start">
            <div className="flex-grow-1">
              <div className="fw-semibold">{courseName}</div>
              {course.description && (
                <small className="text-muted d-block mt-1">{course.description}</small>
              )}
            </div>
            {isLoading ? (
              <span className="badge bg-secondary">
                <span className="spinner-border spinner-border-sm me-1" role="status"></span>
                Loading...
              </span>
            ) : hasLevelProgress ? (
              <span className="badge bg-primary">{completedLevels} / {totalLevels} levels</span>
            ) : xpValue !== undefined && (
              <span className="badge bg-primary">{xpValue} XP</span>
            )}
          </div>
        </label>
      </div>
    );
  };

  const renderSimpleList = () => {
    if (!courses || courses.length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading courses...</span>
          </div>
          <p className="mt-2 mb-0 small">Loading courses...</p>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-2">
        {courses.map((course) => {
          const courseId = course.id || course.slug || course.name;
          const courseName = course.name || course.title;
          return renderCourseCheckbox(course, courseId, courseName);
        })}
      </div>
    );
  };

  const renderDuolingoStructure = () => {
    if (!courseStructure || Object.keys(courseStructure).length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading courses...</span>
          </div>
          <p className="mt-2 mb-0 small">Loading courses...</p>
        </div>
      );
    }

    return (
      <div className="d-flex flex-column gap-4">
        {Object.entries(courseStructure).map(([categoryKey, category]) => {
          if (!category.courses || category.courses.length === 0) return null;

          return (
            <div key={categoryKey}>
              <h6 className="fw-bold mb-3">{category.name}</h6>
              <div className="d-flex flex-column gap-2">
                {category.courses.map((course) => {
                  const courseId = course.id;
                  const courseName = course.title;
                  return renderCourseCheckbox(course, courseId, courseName);
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderKhanAcademyStructure = () => {
    if (!courseStructure || Object.keys(courseStructure).length === 0) {
      return (
        <div className="text-center text-muted py-3">
          <div className="spinner-border spinner-border-sm" role="status">
            <span className="visually-hidden">Loading courses...</span>
          </div>
          <p className="mt-2 mb-0 small">Loading courses...</p>
        </div>
      );
    }

    const allowedSubjects = ['math', 'math_vn'];

    return (
      <div className="d-flex flex-column gap-4">
        {Object.entries(courseStructure).map(([subjectKey, subject]) => {
          if (!allowedSubjects.includes(subjectKey)) return null;
          if (!subject.courses || subject.courses.length === 0) return null;

          return (
            <div key={subjectKey}>
              <h6 className="fw-bold mb-3">{subject.name}</h6>
              <div className="d-flex flex-column gap-2">
                {subject.courses.map((course) => {
                  const courseId = course.course_id || course.slug;
                  const courseName = course.name;
                  return renderCourseCheckbox(course, courseId, courseName);
                })}
              </div>
            </div>
          );
        })}
      </div>
    );
  };

  const renderCourseList = () => {
    if (activityType === 'duolingo') {
      return renderDuolingoStructure();
    } else if (activityType === 'khan_academy') {
      return renderKhanAcademyStructure();
    } else {
      // CodeCombat or other simple list structures
      return renderSimpleList();
    }
  };

  const getTitle = () => {
    switch (activityType) {
      case 'duolingo':
        return 'Chọn Ngôn Ngữ';
      case 'khan_academy':
        return 'Chọn Khóa Học Toán';
      case 'codecombat':
        return 'Chọn Khóa Học';
      default:
        return 'Chọn Khóa Học';
    }
  };

  const getHelpText = () => {
    if (maxCourses > 0) {
      return `Bạn phải chọn ít nhất 1 và tối đa ${maxCourses} khóa học.`;
    }
    return `Chọn một hoặc nhiều khóa học cho ${activityName}`;
  };

  return (
    <div className="course-selector" {...(isTestMode && { 'data-testid': 'course-selector' })}>
      <div className="mb-4">
        <label className="form-label fw-semibold">{getTitle()}</label>
        <small className="form-text text-muted d-block mb-3">
          {getHelpText()}
        </small>

        <div className="border rounded p-3">
          {renderCourseList()}
        </div>

        {/* Error Message */}
        {error && (
          <div
            className="invalid-feedback d-block mt-2"
            {...(isTestMode && { 'data-testid': 'course-selection-error' })}
          >
            {error}
          </div>
        )}

        {/* Selection Count */}
        {localSelectedCourses.size > 0 && (
          <div className="mt-2">
            <small className="text-muted">
              Đã chọn {localSelectedCourses.size} khóa học
              {maxCourses > 0 && ` (tối đa ${maxCourses})`}
            </small>
          </div>
        )}
      </div>
    </div>
  );
};

export default CourseSelector;
